/** 成本议价单展示辅助 */

import { localizedSiteLabel } from '@/i18n/catalogDisplayText';
import { fixedUi } from '@/i18n/fixedUi';
import {
  DISCUSS_STATUS_LABELS,
  DISCUSS_TYPE_LABELS,
} from '@/temu-platform/constants/temuApiCatalog';

export function discussStatusLabel(code) {
  if (code === undefined || code === null || code === '') return '—';
  const known = DISCUSS_STATUS_LABELS[code] || DISCUSS_STATUS_LABELS[Number(code)];
  if (known) return fixedUi(known);
  return String(code);
}

export function discussTypeLabel(code) {
  if (code === undefined || code === null || code === '') return '—';
  const known = DISCUSS_TYPE_LABELS[code] || DISCUSS_TYPE_LABELS[Number(code)];
  if (known) return fixedUi(known);
  return String(code);
}

/** Temu orderStatus：0/1/5 进行中，2 通过，3/4 失败或废弃 */
export function discussStatusModifier(code) {
  const n = Number(code);
  if (n === 2) return 'success';
  if (n === 3 || n === 4) return 'failed';
  return 'pending';
}

export function normalizeDiscussImageUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (raw.startsWith('//')) return `https:${raw}`;
  return raw;
}

export function extractDiscussList(info) {
  if (!info) return [];
  if (Array.isArray(info)) return info;
  if (Array.isArray(info.items)) return info.items;
  if (Array.isArray(info.content)) return info.content;
  if (Array.isArray(info.data)) return info.data;
  if (Array.isArray(info.list)) return info.list;
  return [];
}

export function extractDiscussTotal(info, fallback = 0) {
  if (!info || typeof info !== 'object') return fallback;
  const n = Number(info.count ?? info.total ?? info.totalCount ?? info.totalElements);
  return Number.isFinite(n) ? n : fallback;
}

/** Temu 价格多为「分」；已是小数则原样展示 */
export function formatTemuPriceAmount(amount, currency) {
  if (amount === undefined || amount === null || amount === '') return '—';
  const cur = currency ? `${currency} ` : '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${cur}${amount}`;
  if (Number.isInteger(n) && Math.abs(n) >= 1) {
    return `${cur}${(n / 100).toFixed(2)}`;
  }
  return `${cur}${n}`;
}

export function extractSiteNameList(row) {
  const list = Array.isArray(row?.siteNameList)
    ? row.siteNameList.map((s) => String(s || '').trim()).filter(Boolean)
    : [];
  if (list.length) return list;
  const code = String(row?.siteCode || '').trim();
  return code ? [code] : [];
}

export function formatSiteNameList(row) {
  const list = extractSiteNameList(row).map((name) => localizedSiteLabel(name));
  return list.length ? list.join('、') : '—';
}

export function formatProductSkuIdList(row) {
  const list = Array.isArray(row?.productSkuIdList) ? row.productSkuIdList : [];
  if (list.length) return list.map(String).join('、');
  const one = row?.productSkuId ?? row?.skuId;
  return one != null && String(one).trim() ? String(one) : '—';
}

/** 核价单申报价/建议价：按 SKU 分行（同单一价时各 SKU 共用） */
export function formatReviewSkuPriceLines(row) {
  const currency = row?.priceCurrency || '';
  const suggestCurrency = row?.suggestPriceCurrency || currency;
  const supply = formatTemuPriceAmount(row?.supplyPrice ?? row?.declarePrice, currency);
  const suggest = formatTemuPriceAmount(
    row?.suggestSupplyPrice ?? row?.suggestPrice,
    suggestCurrency,
  );
  const ids = Array.isArray(row?.productSkuIdList)
    ? row.productSkuIdList.map((id) => id).filter((id) => id != null && String(id).trim() !== '')
    : [];
  if (!ids.length) {
    const one = row?.productSkuId ?? row?.skuId;
    if (one != null && String(one).trim() !== '') {
      ids.push(one);
    }
  }
  if (!ids.length) {
    if (supply === '—' && suggest === '—') return { lines: [], text: '—' };
    return {
      lines: [{ key: 'fallback', head: '', supply, suggest }],
      text: `${supply} / ${suggest}`,
    };
  }
  const lines = ids.map((id, idx) => ({
    key: String(id ?? idx),
    head: String(id),
    supply,
    suggest,
  }));
  return {
    lines,
    text: lines.map((l) => `${l.head}: ${l.supply} / ${l.suggest}`).join('\n'),
  };
}

export function formatCanBargain(value) {
  if (value === true || value === 'true' || value === 1 || value === '1') return '是';
  if (value === false || value === 'false' || value === 0 || value === '0') return '否';
  return '—';
}

/** 取 SKU 最新一轮商家报价（serialNumber 最大） */
export function latestCostHistory(sku) {
  const histories = Array.isArray(sku?.costPriceHistories) ? sku.costPriceHistories : [];
  if (!histories.length) return null;
  return histories.reduce((best, cur) => {
    if (!best) return cur;
    return Number(cur?.serialNumber) >= Number(best?.serialNumber) ? cur : best;
  }, null);
}

export function formatCostMoney(amount, currency) {
  if (amount === undefined || amount === null || amount === '') return '—';
  const cur = currency ? `${currency} ` : '';
  return `${cur}${amount}`;
}

/** 商家最新报价：优先 costPriceHistories 中 serialNumber 最大的一轮；否则回退 latestCostPrice */
export function formatSkuQuoteMoney(sku) {
  const last = latestCostHistory(sku);
  if (last && last.costPrice !== undefined && last.costPrice !== null && last.costPrice !== '') {
    return formatCostMoney(last.costPrice, last.currency);
  }
  if (sku?.latestCostPrice !== undefined && sku?.latestCostPrice !== null && sku?.latestCostPrice !== '') {
    return formatCostMoney(sku.latestCostPrice, last?.currency || sku?.suggestCostCurrency);
  }
  return '—';
}

/** 平台建议价 */
export function formatSkuSuggestMoney(sku) {
  return formatCostMoney(sku?.suggestCostPrice, sku?.suggestCostCurrency);
}

/** 多 SKU 时只展示最后一个 SKU 的价格（列表不拼接多个规格） */
export function formatSkuPricesSummary(skus, formatter) {
  const list = Array.isArray(skus) ? skus : [];
  if (!list.length) return '—';
  return formatter(list[list.length - 1]);
}
